import React from 'react';
import { Form, FormOnSubmit } from 'components/form';
import {
  SelfOnboardingStepKey,
  useUpdateWorkerMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { getGraphQLError } from 'utils/error';
import { isNotEmpty } from 'utils';
import { commonClasses } from 'containers/selfOnboarding/steps/common.style';
import {
  SelfOnboardingStepProps,
  SelfOnboardingEmergencyContactFormData,
} from 'containers/selfOnboarding/steps/types';
import {
  StepInfo,
  StepActions,
  StepInfoTitle,
  StepJobsiteInfo,
} from 'containers/selfOnboarding/steps/components';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import { useGetIsSelfOnboardingClosed } from 'containers/selfOnboarding/helpers/useGetIsSelfOnboardingClosed';
import { useSelfOnboardingFormCommonProps } from 'containers/selfOnboarding/helpers/forms';
import {
  getDefaultValues,
  getFormInputs,
  getUpdateInput,
} from './EmergencyContactStep.forms';

export function EmergencyContactStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const {
    state,
    updateState,
    navigation,
    stepConfig,
    localize,
    language,
    jobsiteInvitation,
  } = props;
  const { worker, jobsiteWorker, emergencyContact, contractorId } = state;
  const {
    goToStep,
    goToNextStep,
    getStepNumberInfo,
    state: navigationState,
  } = navigation;
  const { isReview, autoFocusField } = navigationState;
  const stepNumberInfo = getStepNumberInfo();

  const [isSaving, setIsSaving] = React.useState(false);
  const [updateWorker] = useUpdateWorkerMutation();
  const { isSelfOnboardingClosed } = useGetIsSelfOnboardingClosed();

  const onSubmit: FormOnSubmit<SelfOnboardingEmergencyContactFormData> = async (
    data,
    event,
    dirtyFields,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      if (await isSelfOnboardingClosed()) {
        goToStep(SelfOnboardingStepKey.Closed);
        return;
      }

      if (isNotEmpty(dirtyFields)) {
        const input = getUpdateInput(worker, data, dirtyFields);
        await updateWorker({ variables: input });
        updateState({ emergencyContact: data });
      }

      setIsSaving(false);
      goToNextStep();
    } catch (error) {
      event.preventDefault();
      setIsSaving(false);
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    }
  };

  const inputs = React.useMemo(() => getFormInputs(), []);
  const defaultValues = React.useMemo(
    () => getDefaultValues(emergencyContact),
    [emergencyContact],
  );

  const { fieldsConfig, localization } = useSelfOnboardingFormCommonProps(
    stepConfig,
    localize,
  );

  return (
    <div className={commonClasses.stepContainer}>
      <StepInfo>
        <StepInfoTitle
          title={localize(Copy.self_onboarding_emergency_contact_header)}
          subtitle={localize(
            Copy.self_onboarding_emergency_contact_instructions,
          )}
          withBottomBorder
          hideSubtitleOnMobile
          stepNumberInfo={stepNumberInfo}
        />
        <StepJobsiteInfo
          jobsiteInvitation={jobsiteInvitation}
          jobsiteWorker={jobsiteWorker}
          contractorName={contractorId?.label}
        />
      </StepInfo>
      <Form
        inputs={inputs}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        autoFocus={
          (autoFocusField as keyof SelfOnboardingEmergencyContactFormData) ??
          true
        }
        renderBelow={
          <StepActions
            jobsiteInvitation={jobsiteInvitation}
            localize={localize}
            isReview={isReview}
            actions={isReview ? 'all' : 'continue'}
            onSkip={goToNextStep}
            continueActionWithSpinner={isSaving}
          />
        }
        validationTriggers={[language]}
        className={commonClasses.form}
        inputsContainerClassName={commonClasses.formInputsContainer}
        fieldsConfig={fieldsConfig}
        localization={localization}
      />
    </div>
  );
}
